import Hls from "hls.js";

window.addEventListener("load", () => {

	/* =====================================================================
	   GLOBAL: only one player active across ALL webcast blocks on the page
	   Both webcast-clip and webcast-archive dispatch & listen to this event.
	===================================================================== */

	function notifyPlaybackStart(sourceBlock) {
		document.dispatchEvent(new CustomEvent("wc-playback-start", {
			detail: { source: sourceBlock }
		}));
	}

	document.querySelectorAll(".webcast-clip-block").forEach((block) => {
		const startTime = parseInt(block.dataset.startTime, 10) || 0;
		const endTime = parseInt(block.dataset.endTime, 10) || 0;
		const isClip = block.dataset.isClip === "true";
		const clipActive = isClip && endTime > startTime;
		const clipDuration = clipActive ? endTime - startTime : 0;

		const entry = block.querySelector(".wcc-entry");
		const playerBox = block.querySelector(".wcc-player");
		const video = block.querySelector(".wcc-video");
		const closeBtn = block.querySelector(".wcc-player-close");

		const ctrlPlay = block.querySelector(".wcc-ctrl-play");
		const iconPlay = block.querySelector(".wcc-icon-play");
		const iconPause = block.querySelector(".wcc-icon-pause");
		const progressWrap = block.querySelector(".wcc-progress-wrap");
		const progressFill = block.querySelector(".wcc-progress-fill");
		const timeDisplay = block.querySelector(".wcc-time-display");
		const ctrlFullscreen = block.querySelector(".wcc-ctrl-fullscreen");
		const fullVideoLink = block.querySelector(".wcc-full-video-link");

		if (!playerBox || !video) return;

		const entryLangBtns = block.querySelectorAll(".wcc-langs .wcc-lang-btn");
		const playerLangBtns = block.querySelectorAll(".wcc-player-langs .wcc-player-lang-btn");

		let autoStopInterval = null;
		let currentStreamUrl = "";

		// ★ Mutable flag — when true, ALL clip logic is bypassed
		let fullMode = false;

		/* =================================================================
		   HELPERS
		================================================================= */

		function fmtTime(sec) {
			const s = Math.max(0, Math.floor(sec));
			const m = Math.floor(s / 60);
			const ss = s % 60;
			return m + ":" + String(ss).padStart(2, "0");
		}

		/* =================================================================
		   CUSTOM CONTROLS — play / pause
		================================================================= */

		if (ctrlPlay) {
			ctrlPlay.addEventListener("click", () => {
				if (video.paused) video.play().catch(() => {});
				else video.pause();
			});
		}

		video.addEventListener("play", () => {
			if (iconPlay) iconPlay.style.display = "none";
			if (iconPause) iconPause.style.display = "block";
		});

		video.addEventListener("pause", () => {
			if (iconPlay) iconPlay.style.display = "block";
			if (iconPause) iconPause.style.display = "none";
		});

		/* =================================================================
		   CUSTOM PROGRESS BAR
		   - clip mode  → shows 0:00 to clipDuration
		   - full mode  → shows 0:00 to total video duration
		================================================================= */

		function updateProgress() {
			if (fullMode || !clipActive) {
				// Full video: real progress
				if (video.duration && isFinite(video.duration)) {
					const pct = (video.currentTime / video.duration) * 100;
					if (progressFill) progressFill.style.width = pct + "%";
					if (timeDisplay) timeDisplay.textContent = fmtTime(video.currentTime) + " / " + fmtTime(video.duration);
				}
			} else {
				// Clip: progress relative to clip range
				const elapsed = Math.max(0, video.currentTime - startTime);
				const pct = Math.min(100, (elapsed / clipDuration) * 100);
				if (progressFill) progressFill.style.width = pct + "%";
				if (timeDisplay) timeDisplay.textContent = fmtTime(elapsed) + " / " + fmtTime(clipDuration);
			}
		}

		video.addEventListener("timeupdate", updateProgress);

		/* =================================================================
		   CLICK-TO-SEEK on progress bar
		================================================================= */

		if (progressWrap) {
			progressWrap.addEventListener("click", (e) => {
				const rect = progressWrap.getBoundingClientRect();
				const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));

				if (!fullMode && clipActive) {
					video.currentTime = startTime + pct * clipDuration;
				} else if (video.duration && isFinite(video.duration)) {
					video.currentTime = pct * video.duration;
				}
			});
		}

		/* =================================================================
		   FULLSCREEN
		================================================================= */

		if (ctrlFullscreen) {
			ctrlFullscreen.addEventListener("click", () => {
				const wrap = block.querySelector(".wcc-video-wrap");
				if (document.fullscreenElement) {
					document.exitFullscreen();
				} else if (wrap) {
					wrap.requestFullscreen().catch(() => {
						if (video.requestFullscreen) video.requestFullscreen();
					});
				}
			});
		}

		/* =================================================================
		   SEEK CLAMPING (only when NOT in fullMode)
		================================================================= */

		function clampSeek() {
			if (fullMode) return; // ← bypass
			if (video.currentTime < startTime) video.currentTime = startTime;
			else if (video.currentTime > endTime) video.currentTime = endTime;
		}

		if (clipActive) {
			video.addEventListener("seeking", clampSeek);
		}

		/* =================================================================
		   SHOW / HIDE
		================================================================= */

		function showPlayer() {
			if (entry) entry.classList.add("wcc-entry-hidden");
			playerBox.classList.remove("wcc-player-hidden");
			playerBox.classList.add("wcc-player-visible");
			playerBox.scrollIntoView({ behavior: "smooth", block: "center" });
		}

		function hidePlayer() {
			playerBox.classList.remove("wcc-player-visible");
			playerBox.classList.add("wcc-player-hidden");
			if (entry) entry.classList.remove("wcc-entry-hidden");
		}

		/* =================================================================
		   CLOSE — also resets fullMode back to clip mode
		================================================================= */

		function closePlayer() {
			video.pause();
			if (video._hlsInstance) { video._hlsInstance.destroy(); video._hlsInstance = null; }
			video.src = ""; video.load();

			if (autoStopInterval) { clearInterval(autoStopInterval); autoStopInterval = null; }

			// Reset fullMode
			fullMode = false;
			if (fullVideoLink) {
				fullVideoLink.classList.remove("wcc-full-active");
				fullVideoLink.innerHTML = '<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><polygon points="5,3 19,12 5,21"/></svg> Watch full video';
			}

			entryLangBtns.forEach((b) => {
				b.classList.remove("active");
				const ic = b.querySelector(".wcc-lang-play");
				if (ic) ic.textContent = "\u25BA";
			});
			playerLangBtns.forEach((b) => {
				b.classList.remove("active");
				const ic = b.querySelector(".wcc-plb-icon");
				if (ic) ic.textContent = "\u25BA";
			});

			if (progressFill) progressFill.style.width = "0%";
			if (timeDisplay) timeDisplay.textContent = "0:00 / 0:00";

			hidePlayer();
		}

		if (closeBtn) closeBtn.addEventListener("click", closePlayer);

		/* =================================================================
		   GLOBAL: stop this player if another block starts playing
		================================================================= */

		document.addEventListener("wc-playback-start", (e) => {
			if (e.detail.source !== block) {
				// Another block started playing — silently stop ours
				video.pause();
				if (video._hlsInstance) { video._hlsInstance.destroy(); video._hlsInstance = null; }
				video.src = ""; video.load();
				if (autoStopInterval) { clearInterval(autoStopInterval); autoStopInterval = null; }

				fullMode = false;
				if (fullVideoLink) {
					fullVideoLink.classList.remove("wcc-full-active");
					fullVideoLink.innerHTML = '<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><polygon points="5,3 19,12 5,21"/></svg> Watch full video';
				}

				entryLangBtns.forEach((b) => {
					b.classList.remove("active");
					const ic = b.querySelector(".wcc-lang-play");
					if (ic) ic.textContent = "\u25BA";
				});
				playerLangBtns.forEach((b) => {
					b.classList.remove("active");
					const ic = b.querySelector(".wcc-plb-icon");
					if (ic) ic.textContent = "\u25BA";
				});

				if (progressFill) progressFill.style.width = "0%";
				if (timeDisplay) timeDisplay.textContent = "0:00 / 0:00";

				hidePlayer();
			}
		});

		/* =================================================================
		   PLAY URL
		   Respects fullMode: if true → seek to fromTime, no clamp
		                       if false + clip → seek to startTime, clamp
		================================================================= */

		function playUrl(url, fromTime, isLive) {
			currentStreamUrl = url;

			// ★ Notify all other blocks to stop
			notifyPlaybackStart(block);

			if (isLive && /\.mp4(\?|$)/i.test(url)) {
				url = url.replace(/\.mp4(\?|$)/i, "/index.m3u8$1");
			}

			const useHLS = /\.m3u8(\?|$)/i.test(url) || isLive === true;

			// ★ Key logic: in fullMode → no clip seek
			const seekTo = (!fullMode && clipActive) ? startTime : (fromTime || 0);

			showPlayer();

			const cleanNew = url.split("?")[0].split("#")[0];
			const cleanCurrent = (video.src || "").split("?")[0].split("#")[0];

			if (cleanNew === cleanCurrent) {
				video.currentTime = seekTo;
				video.play();
				if (!fullMode) setupAutoStop();
				return;
			}

			if (video._hlsInstance) { video._hlsInstance.destroy(); video._hlsInstance = null; }

			if (useHLS && Hls.isSupported()) {
				const hls = new Hls({ enableWorker: true, lowLatencyMode: true });
				hls.loadSource(url);
				hls.attachMedia(video);
				hls.on(Hls.Events.MANIFEST_PARSED, () => {
					if (seekTo > 0 && !isLive) video.currentTime = seekTo;
					video.play().catch(() => {});
					if (!fullMode) setupAutoStop();
				});
				hls.on(Hls.Events.ERROR, (_event, data) => {
					if (data.fatal) {
						if (data.type === Hls.ErrorTypes.NETWORK_ERROR) hls.startLoad();
						else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) hls.recoverMediaError();
						else hls.destroy();
					}
				});
				video._hlsInstance = hls;

			} else if (useHLS && video.canPlayType("application/vnd.apple.mpegurl")) {
				video.src = url;
				video.addEventListener("loadedmetadata", function onMeta() {
					if (seekTo > 0 && !isLive) video.currentTime = seekTo;
					video.play().catch(() => {});
					if (!fullMode) setupAutoStop();
					video.removeEventListener("loadedmetadata", onMeta);
				});

			} else {
				video.src = url;
				video.load();
				if (seekTo > 0) {
					video.addEventListener("loadedmetadata", function onMeta() {
						video.currentTime = seekTo;
						video.play();
						if (!fullMode) setupAutoStop();
						video.removeEventListener("loadedmetadata", onMeta);
					});
				} else {
					video.play().catch(() => {});
					if (!fullMode) setupAutoStop();
				}
			}
		}

		/* =================================================================
		   AUTO-STOP (skipped in fullMode)
		================================================================= */

		function setupAutoStop() {
			if (autoStopInterval) clearInterval(autoStopInterval);
			if (fullMode) return; // ← bypass

			if (clipActive) {
				autoStopInterval = setInterval(() => {
					if (fullMode) { clearInterval(autoStopInterval); return; }
					if (video.currentTime >= endTime) {
						video.pause();
						video.currentTime = endTime;
						clearInterval(autoStopInterval);
						autoStopInterval = null;
					}
				}, 400);
			}
		}

		/* =================================================================
		   ★ WATCH FULL VIDEO
		   Switches to fullMode → replays same stream from 0:00
		   with no clamping, no auto-stop, real duration in progress
		================================================================= */

		if (fullVideoLink) {
			fullVideoLink.addEventListener("click", (e) => {
				e.preventDefault();

				// Activate full mode
				fullMode = true;

				// Kill auto-stop
				if (autoStopInterval) { clearInterval(autoStopInterval); autoStopInterval = null; }

				// Update link UI
				fullVideoLink.classList.add("wcc-full-active");
				fullVideoLink.textContent = "▶ Playing full session";

				// Replay same stream from the very beginning
				if (currentStreamUrl) {
					// Force reload from 0 — even if same URL
					// We need to go through the full load path since
					// the "same file" shortcut would just seek
					video.pause();

					if (video._hlsInstance) {
						video._hlsInstance.destroy();
						video._hlsInstance = null;
					}

					// Clear src to force a fresh load
					video.removeAttribute("src");
					video.load();

					// Small delay to ensure clean state, then reload
					setTimeout(() => {
						playUrl(currentStreamUrl, 0, false);
					}, 50);
				}
			});
		}

		/* =================================================================
		   SYNC ACTIVE STATES
		================================================================= */

		function syncActive(activeIdx) {
			entryLangBtns.forEach((b, i) => {
				b.classList.toggle("active", i === activeIdx);
				const ic = b.querySelector(".wcc-lang-play");
				if (ic) ic.textContent = i === activeIdx ? "\u25A0" : "\u25BA";
			});
			playerLangBtns.forEach((b, i) => {
				b.classList.toggle("active", i === activeIdx);
				const ic = b.querySelector(".wcc-plb-icon");
				if (ic) ic.textContent = i === activeIdx ? "\u25A0" : "\u25BA";
			});
		}

		/* =================================================================
		   ENTRY LANG CLICKS
		================================================================= */

		entryLangBtns.forEach((btn, idx) => {
			btn.addEventListener("click", () => {
				const b64 = btn.dataset.b64;
				if (!b64) return;
				if (btn.classList.contains("active")) { closePlayer(); return; }

				let url = "";
				try { url = atob(b64); } catch { return; }

				// If clicking a lang from compact row, reset to clip mode
				fullMode = false;
				if (fullVideoLink) {
					fullVideoLink.classList.remove("wcc-full-active");
					fullVideoLink.innerHTML = '<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><polygon points="5,3 19,12 5,21"/></svg> Watch full video';
				}

				syncActive(idx);
				playUrl(url, 0, (btn.dataset.videoType || "archive") === "live");
			});
		});

		/* =================================================================
		   SIDEBAR LANG CLICKS (keep current time + current mode)
		================================================================= */

		playerLangBtns.forEach((btn, idx) => {
			btn.addEventListener("click", () => {
				const b64 = btn.dataset.b64;
				if (!b64) return;

				// ★ If clicking the already-active lang → stop video
				if (btn.classList.contains("active")) {
					closePlayer();
					return;
				}

				let url = "";
				try { url = atob(b64); } catch { return; }

				// Capture time before switching
				const currentTime = (!video.paused && video.currentTime > 0)
					? Math.floor(video.currentTime)
					: ((!fullMode && clipActive) ? startTime : 0);

				syncActive(idx);

				// Force fresh load to apply new lang at same time
				if (video._hlsInstance) { video._hlsInstance.destroy(); video._hlsInstance = null; }
				video.removeAttribute("src");
				video.load();

				setTimeout(() => {
					playUrl(url, currentTime, (btn.dataset.videoType || "archive") === "live");
				}, 50);
			});
		});

		/* =================================================================
		   THUMBNAIL PLAY (first lang)
		================================================================= */

		const thumbPlay = block.querySelector(".wcc-thumb-play");
		if (thumbPlay) {
			thumbPlay.addEventListener("click", () => {
				if (entryLangBtns.length > 0) entryLangBtns[0].click();
			});
		}
	});
});